<template>
    <div class="reporting-unit-workflow-tab" data-testid="reporting-unit-workflow-tab">
        <!-- Profit & Loss Table -->
        <v-card class="d-flex flex-column pa-2" outlined>
            <h5 class="font-weight-medium">Profit & Loss</h5>
            <lb-datatablenew
                data-testid="reporting-unit-workflow-tab__pl-table"
                :headers="mainHeaders"
                :items="plItems"
                :enableslot="['proforma', 'forecast']"
                :hide-default-footer="true"
                class="elevation-0"
            >
                <template v-slot:proforma="{ item }">
                    <StatusIndicator 
                        :variant="item.proforma ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :customLabel="item.proforma ? 'Yes' : 'No'"
                        size="sm"
                    />
                </template>
                <template v-slot:forecast="{ item }">
                    <StatusIndicator 
                        :variant="item.forecast ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :customLabel="item.forecast ? 'Yes' : 'No'"
                        size="sm"
                    />
                </template>
            </lb-datatablenew>
        </v-card>

        <!-- Balance Sheet Table -->
        <v-card class="d-flex flex-column pa-2" outlined>
            <h5 class="font-weight-medium">Balance Sheet</h5>
            <lb-datatablenew
                data-testid="reporting-unit-workflow-tab__bs-table"
                :headers="mainBsHeaders"
                :items="bsItems"
                :enableslot="['proforma', 'forecast']"
                :hide-default-footer="true"
                class="elevation-0"
            >
                <template v-slot:proforma="{ item }">
                    <StatusIndicator 
                        :variant="item.proforma ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :customLabel="item.proforma ? 'Yes' : 'No'"
                        size="sm"
                    />
                </template>
                <template v-slot:forecast="{ item }">
                    <StatusIndicator 
                        :variant="item.forecast ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :customLabel="item.forecast ? 'Yes' : 'No'"
                        size="sm"
                    />
                </template>
            </lb-datatablenew>
        </v-card>

        <!-- Others Table -->
        <v-card class="d-flex flex-column pa-2" outlined>
            <h5 class="font-weight-medium">Others</h5>
            <lb-datatablenew
                data-testid="reporting-unit-workflow-tab__others-table"
                :headers="otherHeaders"
                :items="otherItems"
                :enableslot="['status']"
                :hide-default-footer="true"
                class="elevation-0"
            >
                <template v-slot:status="{ item }">
                        <StatusIndicator 
                        :variant="item.status ? STATUS_VARIANTS.APPROVED : STATUS_VARIANTS.DISABLED"
                        :customLabel="item.status ? 'Yes' : 'No'"
                        size="sm"
                    />
                </template>
            </lb-datatablenew>
        </v-card>
    </div>
</template>

<script>
import StatusIndicator from '../../../components/StatusIndicator.vue';
import { STATUS_VARIANTS } from '../../../utils/statusConstants';

export default {
    name: 'ReportingUnitWorkflowTab',
    components: {
        StatusIndicator
    },
    props: {
        detail: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            STATUS_VARIANTS
        }
    },
    computed: {
        mainHeaders() {
            return [
                { text: 'Workflow Type', value: 'type', sortable: false },
                { text: this.$nova.labelsName('my_reporting_units_page', 'proforma_shortform_label', 'Proforma'), value: 'proforma', sortable: false },
                { text: this.$nova.labelsName('my_reporting_units_page', 'forecast_shortform_label', 'Forecast'), value: 'forecast', sortable: false }
            ];
        },
         mainBsHeaders() {
            return [
                { text: 'Workflow Type', value: 'type', sortable: false },
                { text: this.$nova.labelsName('my_reporting_units_page', 'proforma_shortform_label_balancesheet', 'Proforma'), value: 'proforma', sortable: false },
                { text: this.$nova.labelsName('my_reporting_units_page', 'forecast_shortform_label_balancesheet', 'Forecast'), value: 'forecast', sortable: false }
            ];
        },
        otherHeaders() {
            return [
                { text: 'Features', value: 'feature', sortable: false },
                { text: 'Status', value: 'status', sortable: false },
            ];
        },
        plItems() {
             if (!this.detail) return [];
             const d = this.detail;
             return [
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'Local')} Control`,
                     proforma: d.localcontrol_proforma,
                     forecast: d.localcontrol_forecast
                 },
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'Local')} Management`,
                     proforma: d.localmanagement_proforma,
                     forecast: d.localmanagement_forecast
                 },
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'Regional')} Control`,
                     proforma: d.regionalcontrol_proforma,
                     forecast: d.regionalcontrol_forecast
                 },
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'Regional')} Management`,
                     proforma: d.regionalmanagement_proforma,
                     forecast: d.regionalmanagement_forecast
                 }
             ];
        },
        bsItems() {
             if (!this.detail) return [];
             const d = this.detail;
             return [
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'Local')} Control`,
                     proforma: d.localcontrol_proforma_bs ?? d.localcontrol_proforma,
                     forecast: d.localcontrol_forecast_bs ?? d.localcontrol_forecast
                 },
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_local_key_label', 'Local')} Management`,
                     proforma: d.localmanagement_proforma_bs ?? d.localmanagement_proforma,
                     forecast: d.localmanagement_forecast_bs ?? d.localmanagement_forecast
                 },
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'Regional')} Control`,
                     proforma: d.regionalcontrol_proforma_bs ?? d.regionalcontrol_proforma,
                     forecast: d.regionalcontrol_forecast_bs ?? d.regionalcontrol_forecast
                 },
                 {
                     type: `${this.$nova.labelsName('my_reporting_units_page', 'commentary_regional_key_label', 'Regional')} Management`,
                     proforma: d.regionalmanagement_proforma_bs ?? d.regionalmanagement_proforma,
                     forecast: d.regionalmanagement_forecast_bs ?? d.regionalmanagement_forecast
                 }
             ];
        },
        otherItems() {
            if (!this.detail) return [];
            return [
                {
                    feature: 'Bulk Approve Control',
                    status: this.detail.bulkapprove_control
                },
                {
                    feature: 'SOD Enabled',
                    status: this.detail.sodEnabled
                },
                {
                    feature: 'Auto Submission Enabled',
                    status: this.detail.autoSubmissionEnabled
                }
            ];
        }
    }
}
</script>

<style scoped>
    .reporting-unit-workflow-tab {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-column-gap: 12px;
        grid-row-gap: 12px;
    }
    .reporting-unit-workflow-tab h5{
        padding: 8px 0;
    }
</style>
